import React, { useState } from 'react';
import { Wand2, Copy, Check, AlertCircle, Youtube, Search } from 'lucide-react';

// API key for YouTube Data API
const API_KEY = 'AIzaSyBXTNdCq_UjVxhns41F2dQKm2hWq_IPPNE';

interface SimilarVideo {
  id: string;
  title: string;
  tags: string[];
  viewCount: string;
}

const fetchSimilarVideos = async (query: string): Promise<SimilarVideo[]> => {
  try {
    // First, search for similar videos
    const searchResponse = await fetch(
      `https://www.googleapis.com/youtube/v3/search?part=id&q=${encodeURIComponent(query)}&type=video&maxResults=10&key=${API_KEY}`
    );
    const searchData = await searchResponse.json();
    
    if (!searchData.items) {
      throw new Error('No videos found');
    }

    // Get video details including tags
    const videoIds = searchData.items.map((item: any) => item.id.videoId).join(',');
    const videoResponse = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=snippet,statistics&id=${videoIds}&key=${API_KEY}`
    );
    const videoData = await videoResponse.json();

    return videoData.items.map((item: any) => ({
      id: item.id,
      title: item.snippet.title,
      tags: (item.snippet.tags || []).filter(Boolean), // Remove any null/empty tags
      viewCount: item.statistics.viewCount
    }));
  } catch (error) {
    console.error('Error fetching videos:', error);
    throw error;
  }
};

// Process and combine tags from similar videos
const processTags = (videos: SimilarVideo[]): string[] => {
  // Get all tags from videos
  const allTags = videos.flatMap(video => video.tags);
  
  // Function to check if a tag is relevant to the title
  const isRelevantTag = (tag: string, title: string): boolean => {
    // Normalize strings by converting to lowercase and removing special characters
    const normalizeText = (text: string) => text.toLowerCase().replace(/[^a-z0-9\s]/g, '');
    
    const normalizedTitle = normalizeText(title);
    const normalizedTag = normalizeText(tag);
    
    // Split into words and filter out empty strings and common words
    const commonWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by']);
    const titleWords = normalizedTitle.split(/\s+/).filter(word => word.length > 2 && !commonWords.has(word));
    const tagWords = normalizedTag.split(/\s+/).filter(word => word.length > 2 && !commonWords.has(word));
    
    // Calculate relevance score based on word matches
    let relevanceScore = 0;
    
    for (const tagWord of tagWords) {
      // Check for exact word matches (highest relevance)
      if (titleWords.includes(tagWord)) {
        relevanceScore += 2;
        continue;
      }
      
      // Check for partial matches for longer words
      if (tagWord.length > 4) {
        for (const titleWord of titleWords) {
          if (titleWord.length > 4) {
            if (titleWord.includes(tagWord) || tagWord.includes(titleWord)) {
              relevanceScore += 1;
              break;
            }
          }
        }
      }
    }
    
    // Calculate minimum required score based on tag length
    const minRequiredScore = Math.ceil(tagWords.length * 0.5); // Require 50% relevance
    
    return relevanceScore >= minRequiredScore;
  };
  
  // Count tag frequency
  const tagFrequency = allTags.reduce((acc, tag) => {
    acc[tag] = (acc[tag] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Filter and sort tags by frequency and relevance
  const sortedTags = Object.entries(tagFrequency).filter(([tag]) => 
    // Keep tags that are relevant to the video titles
    videos.some(video => isRelevantTag(tag, video.title))
  )
    .sort(([, a], [, b]) => b - a)
    .map(([tag]) => tag);
  
  // Return top 20 most used tags
  return sortedTags.slice(0, 20);
};

const YouTubeTagGenerator: React.FC = () => {
  const [title, setTitle] = useState('');
  const [generatedTags, setGeneratedTags] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [similarVideos, setSimilarVideos] = useState<SimilarVideo[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (title.trim()) {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch similar videos using YouTube API
        const videos = await fetchSimilarVideos(title);
        setSimilarVideos(videos);

        // Process and get most used tags from similar videos
        const tags = processTags(videos);
        
        setGeneratedTags(tags);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to fetch similar videos. Please try again.');
        setIsLoading(false);
      }
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedTags.join(', '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <section id="youtube-generator" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-red-100 text-red-800 px-4 py-1 rounded-full text-sm font-medium">
            AI-Powered Tag Generation
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          YouTube Tag Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter your video title"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white"
              />
            </div>

          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              disabled={!title.trim()}
              className={`bg-red-600 text-white py-4 px-8 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 relative ${
                !title.trim() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5" />
                  <span>Generate Tags</span>
                </>
              )}
            </button>
          </div>

          {similarVideos.length > 0 && (
            <div className="mt-8 bg-red-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-red-500" />
                Tags Sourced from These Similar Videos:
              </h4>
              <div className="space-y-4">
                {similarVideos.map((video) => (
                  <div key={video.id} className="flex items-center gap-4 text-sm">
                    <Youtube className="w-4 h-4 text-red-500" />
                    <span className="text-gray-700">{video.title}</span>
                    <span className="text-gray-500">({parseInt(video.viewCount).toLocaleString()} views)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}

          {generatedTags.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-red-500" />
                  Top 20 Most Relevant Tags from Similar Videos:
                </h3>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  {copied ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                  {copied ? 'Copied!' : 'Copy Tags'}
                </button>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  {generatedTags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm text-center hover:bg-red-200 transition-colors"
                      title={tag}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: These tags are sourced from successful videos in your niche!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default YouTubeTagGenerator;